// -----------------------------------------------------------------------------
// PRINT PAGES — the exported PDF boards P0–P3 as real A4 pages (Part 5).
//   ComponentPdf = P1 cover (portrait) → P1.1 signing page (portrait 2 × 4, when "Include signing page") →   [portrait since round 22]
//                  P2 component summary (35 rows per page, every issue, voided struck through; codes link to P3) →
//                  P3 one issue page per included issue (log newest first; a long log continues on the next page).
//   MasterPdf    = P0 master summary (own PDF, date only, no version; status donut since round 24).
// Round 24 (v0.7.0, boards Cover / ComponentSummary / MasterSummary / IssueDetail):
//   #1 cover photo 430 px tall, fields right under it · #3/#8/#14 P2 35 rows of 24 px, no row lines ·
//   #4 P2 issue codes = links to the issue's P3 page (#issue-<code>; Chrome keeps them in the saved PDF) ·
//   #9 P0 two-ring donut (outer Open + Critical / Closed / Voided, inner Overdue) left of narrower discipline bars ·
//   #6/#7/#11/#17 P3 field column 200 px + description card filling the rest, its height fixed to the column, text as
//   typed, never grows (cut off when longer) · #15/#16 P3 picture card 220 px, at most 2 (chosen in A5.1) ·
//   #5/#10/#15 the newest log snapshot 160 × 160 px on the right of its entry.
// Data: PdfData (stored with the version, rpc issues_publish / issues_publish_get) + PrintContext (pictures).
// Rules live in ./pdf.ts. Layout in public/styles.css, section "PRINT". The project code (BIM-XXX) is never printed.
// Pages that measure themselves (IssuePages) carry data-measuring until done; printer.tsx waits for that.
// -----------------------------------------------------------------------------
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { Discipline, IssueCounts, IssuesOverview, PdfData, PdfIssue, PrintContext, SignoffState } from "../../../core/api/types";
import { formatDate } from "../../../core/format";
import { STATUS_CLASS } from "../../../core/ui/kit";
import { MAX_DISCIPLINES } from "../logic";
import { chunk, issuePages, openCounts, pdfTitle, splitLog, SUMMARY_ROWS } from "./pdf";

const MAX_SIGNATORIES = 8;

function Footer({ ctx, page }: { ctx: PrintContext; page: number }) {
  return (
    <div className="pp-foot">
      <p>Prepared by <b>{ctx.prepared_by}</b></p>
      <div className="row gap16"><span className="pp-page">Page {page}</span>{ctx.prepared_logo && <img className="pp-logo" src={ctx.prepared_logo} alt="" />}</div>
    </div>
  );
}

const VerChip = ({ v }: { v: string }) => <span className="pp-ver" data-version={v}>{v}</span>;

/** Band shared by the cover and the signing page (full page width, A4 portrait since round 22). */
function Band({ data }: { data: PdfData }) {
  return (
    <div className="pp-band">
      <div><p className="pp-band-k">{data.report_title}</p>
        <div className="row gap10" style={{ justifyContent: "center" }}><h1>{pdfTitle(data.component.prefix, data.component.name)}</h1><VerChip v={data.version} /></div></div>
    </div>
  );
}

function CoverPage({ data, ctx }: { data: PdfData; ctx: PrintContext }) {
  return (
    <section className="pp cov" data-page="cover">
      <Band data={data} />
      <div className="pp-cover">
        <div className="pp-photo">{ctx.photo && <img src={ctx.photo} alt="" />}</div>
        <div className="pp-cover-fields">
          <div><p className="k">Project title</p><p className="v">{data.project.name}</p></div>
          <div><p className="k">Date of report</p><p className="v">{formatDate(data.report_date)}</p></div>
          <div><p className="k">Client</p><p className="v">{data.project.client}</p>{ctx.client_logo && <img className="pp-cover-logo" src={ctx.client_logo} alt="" />}</div>
          <div><p className="k">Prepared by</p><p className="v">{data.project.prepared_by}</p>{ctx.prepared_logo && <img className="pp-cover-logo" src={ctx.prepared_logo} alt="" />}</div>
          {/* v0.9.0 (round 30 #9, round 31 #1): Project owner + its logo in its own row under Client; left out when not set */}
          {(data.project.owner ?? ctx.owner) && <div data-cover-owner="1"><p className="k">Project owner</p><p className="v">{data.project.owner ?? ctx.owner}</p>
            {ctx.owner_logo && <img className="pp-cover-logo" src={ctx.owner_logo} alt="" />}</div>}
        </div>
      </div>
    </section>
  );
}

function SigningPage({ data }: { data: PdfData }) {
  const boxes = [...data.signatories, ...Array.from({ length: Math.max(0, MAX_SIGNATORIES - data.signatories.length) }, () => null)];
  const line = (k: string, v: string) => <div className="pp-sig-line"><span>{k}</span><b>{v}</b></div>;
  return (
    <section className="pp cov" data-page="signing">
      <Band data={data} />
      <div className="pp-sign">
        <div className="pp-sign-head"><h2>Sign-off</h2><p>{data.project.name} · Report date {formatDate(data.report_date)}</p></div>
        <div className="pp-sig-grid">
          {boxes.slice(0, MAX_SIGNATORIES).map((s, k) => (
            <div key={k} className="pp-sig" data-signatory={s?.name ?? ""}>
              <p className="pp-sig-k">Signature</p><div className="pp-sig-space" />
              {line("Name", s?.name ?? "")}{line("Title", s?.title ?? "")}{line("Company", s?.company ?? "")}{line("Date", s?.sign_date ? formatDate(s.sign_date) : "")}
            </div>))}
        </div>
      </div>
    </section>
  );
}

/** Anchor of an issue's P3 page — P2 codes link to it (round 24 #4). */
export const issueAnchor = (code: string) => `issue-${code}`;

function SummaryPages({ data, ctx, firstPage }: { data: PdfData; ctx: PrintContext; firstPage: number }) {
  const pages = chunk(data.issues, SUMMARY_ROWS);
  const linked = new Set(issuePages(data.issues, data.include).map((i) => i.code));     // only issues that get a P3 page
  const codes = data.disciplines.map((d) => d.code);
  const open = openCounts(data.issues, codes);
  return <>{pages.map((rows, p) => (
    <section key={p} className="pp" data-page="summary">
      <div className="pp-body">
        <p className="pp-eyebrow">{data.report_title} · Component summary</p>
        <div className="row gap10 pp-title"><h2>{pdfTitle(data.component.prefix, data.component.name)}</h2><VerChip v={data.version} /></div>
        {p === 0 && <div className="pp-chips">
          <div className="pp-chip total"><span>Total open</span><b data-open-total>{open.total}</b></div>
          {data.disciplines.map((d) => <div key={d.code} className="pp-chip" style={{ background: d.colour + "1a", color: d.colour }}><span>{d.code}</span><b style={{ color: "#1a1a18" }}>{open.by[d.code] ?? 0}</b></div>)}
          {Array.from({ length: Math.max(0, MAX_DISCIPLINES - data.disciplines.length) }, (_, k) => <div key={`s${k}`} />)}
        </div>}
        <p className="pp-note">Page {p + 1} of {pages.length} · up to {SUMMARY_ROWS} issues per page</p>
        <table className="pp-table sum">
          <thead><tr><th>Issue</th><th>Discipline</th><th>Date created</th><th>Target date</th><th>Status</th><th>Latest action by</th></tr></thead>
          <tbody>{rows.map((i) => (
            <tr key={i.code} data-issue={i.code} className={i.status === "Voided" ? "voided" : ""}>
              <td className="code">{linked.has(i.code) ? <a className="pp-link" href={`#${issueAnchor(i.code)}`} data-pdf-link={i.code}>{i.code}</a> : i.code}</td><td>{i.discipline}</td><td>{formatDate(i.created_on)}</td><td>{formatDate(i.target_date)}</td>
              <td><span className={`pill ${STATUS_CLASS[i.status]}`}>{i.status}</span></td><td>{i.latest_action_by ?? "—"}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
      <Footer ctx={ctx} page={firstPage + p} />
    </section>))}</>;
}

/** P3 pictures per issue code (v0.6.0, print/snaps.ts) — URLs ready to show. */
export type PdfSnaps = Record<string, { pictures: string[]; total: number; log: { entry_date: string; note: string; url: string } | null }>;

const LogEntry = ({ e, pic }: { e: PdfIssue["log"][number]; pic?: string | null }) => (
  <div className="pp-log-entry">
    <div className="pp-log-text"><p className="d">{formatDate(e.entry_date)}</p><p className="n">{e.note}</p><p className="by">Action by: {e.action_by || "—"}</p></div>
    {pic && <div className="pp-log-snap" data-log-snap="pdf"><img src={pic} alt="" /></div>}
  </div>
);
/** the newest log snapshot belongs to the entry with the same date and note (round 14: only that one prints) */
const logPic = (s: PdfSnaps[string] | undefined, e: PdfIssue["log"][number]) =>
  s?.log && s.log.entry_date === e.entry_date && s.log.note === e.note ? s.log.url : null;

/** Fixed card, 220 px tall (round 24 #15); at most 2 snapshots side by side — the 2 chosen in the A5.1 viewer, else the 2 newest. */
export const PRINT_PICTURES = 2;
function SnapGrid({ s }: { s: PdfSnaps[string] | undefined }) {
  if (!s || s.pictures.length === 0) return null;
  const shown = s.pictures.slice(0, PRINT_PICTURES);
  return (
    <div className="pp-snaps" data-snapshots={s.total} style={{ gridTemplateColumns: `repeat(${shown.length}, 1fr)` }}>
      {shown.map((u, k) => <div key={k}><img src={u} alt="" /></div>)}
      {s.total > shown.length && <span className="pp-snaps-more">Showing {shown.length} of {s.total} snapshots</span>}
    </div>);
}

function IssueHead({ data, i, continued }: { data: PdfData; i: PdfIssue; continued?: boolean }) {
  return <>
    <p className="pp-eyebrow">{pdfTitle(data.component.prefix, data.component.name)} · {data.version}</p>
    <div className="pp-issue-head">
      <div className="row gap8"><h2 className="code">{i.code}{continued && <span className="pp-cont"> (continued)</span>}</h2>
        {!continued && <><span className={`pill ${STATUS_CLASS[i.status]}`}>{i.status}</span><span className="pill p-tag">{i.discipline}</span></>}</div>
      {!continued && <div className="pp-right"><p className="k">Date created</p><p className="v">{formatDate(i.created_on)}</p></div>}
    </div>
  </>;
}

function IssueBody({ i, snaps }: { i: PdfIssue; snaps?: PdfSnaps }) {
  return <>
    <SnapGrid s={snaps?.[i.code]} />
    <div className="pp-desc-row" data-desc-row="1">
      <div className="pp-fieldcol">
        <div className="pp-field"><p className="k">Gridline</p><p className="v">{i.gridline || "—"}</p></div>
        <div className="pp-field"><p className="k">Location</p><p className="v">{i.location || "—"}</p></div>
        <div className="pp-field"><p className="k">Target date</p><p className="v">{formatDate(i.target_date)}</p></div>
        <div className="pp-field"><p className="k">RFI reference</p><p className="v">{i.rfi_ref || "—"}</p></div>
        <div className="pp-field"><p className="k">Drawings reference</p><p className="v">{i.drawing_ref || "—"}</p></div>
      </div>
      <div className="pp-desc"><div className="pp-desc-in"><p className="k">Issue Description</p><p className="v">{i.description}</p></div></div>
    </div>
    <p className="pp-log-title">Resolution log <span>(newest first)</span></p>
  </>;
}

/**
 * P3 pages. First render (data-measuring): each issue on one page with its whole log + one empty continuation page;
 * the layout effect measures how much log fits and re-renders with the log split over pages (pdf.ts splitLog).
 */
function IssuePages({ data, ctx, firstPage, snaps }: { data: PdfData; ctx: PrintContext; firstPage: number; snaps?: PdfSnaps }) {
  const list = issuePages(data.issues, data.include);
  const root = useRef<HTMLDivElement>(null);
  const [split, setSplit] = useState<number[][] | null>(null);
  useLayoutEffect(() => {
    if (split || !root.current) return;
    const room = (page: Element): number => {
      const log = page.querySelector(".pp-log") as HTMLElement, foot = page.querySelector(".pp-foot") as HTMLElement;
      return foot.getBoundingClientRect().top - log.getBoundingClientRect().top - 28;   // 28 px: "Continues on next page →"
    };
    const pages = [...root.current.querySelectorAll("[data-measure-issue]")];
    const contRoom = room(root.current.querySelector("[data-measure-cont]")!);
    setSplit(pages.map((pg) => {
      const hs = [...pg.querySelectorAll(".pp-log-entry")].map((e) => (e as HTMLElement).getBoundingClientRect().height + 12);
      return splitLog(hs, room(pg), contRoom);
    }));
  }, [split, data]);

  if (!split) return (
    <div ref={root} data-measuring="1">
      {list.map((i) => (
        <section key={i.code} className="pp" data-measure-issue={i.code}>
          <div className="pp-body"><IssueHead data={data} i={i} /><IssueBody i={i} snaps={snaps} /><div className="pp-log">{i.log.map((e, k) => <LogEntry key={k} e={e} pic={logPic(snaps?.[i.code], e)} />)}</div></div>
          <Footer ctx={ctx} page={0} />
        </section>))}
      <section className="pp" data-measure-cont="1"><div className="pp-body">{list[0] && <IssueHead data={data} i={list[0]} continued />}<div className="pp-log" /></div><Footer ctx={ctx} page={0} /></section>
    </div>);

  let page = firstPage;
  const out: ReactNode[] = [];
  list.forEach((i, n) => {
    let from = 0;
    split[n].forEach((count, part) => {
      const entries = i.log.slice(from, from + count); from += count;
      const more = part < split[n].length - 1;
      out.push(
        <section key={`${i.code}-${part}`} className="pp" data-page="issue" data-issue={i.code} data-part={part} id={part === 0 ? issueAnchor(i.code) : undefined}>
          <div className="pp-body">
            <IssueHead data={data} i={i} continued={part > 0} />
            {part === 0 && <IssueBody i={i} snaps={snaps} />}
            <div className="pp-log">{entries.map((e, k) => <LogEntry key={k} e={e} pic={logPic(snaps?.[i.code], e)} />)}</div>
            {more && <p className="pp-more">Continues on next page →</p>}
          </div>
          <Footer ctx={ctx} page={page++} />
        </section>);
    });
  });
  return <>{out}</>;
}

/** One component PDF (P1 → P1.1 → P2 → P3). */
export function ComponentPdf({ data, ctx, snaps }: { data: PdfData; ctx: PrintContext; snaps?: PdfSnaps }) {
  const summaryFirst = data.with_signing_page ? 3 : 2;
  const issuesFirst = summaryFirst + chunk(data.issues, SUMMARY_ROWS).length;
  return (
    <div data-pdf={`${data.component.prefix} ${data.version}`}>
      <CoverPage data={data} ctx={ctx} />
      {data.with_signing_page && <SigningPage data={data} />}
      <SummaryPages data={data} ctx={ctx} firstPage={summaryFirst} />
      <IssuePages data={data} ctx={ctx} firstPage={issuesFirst} snaps={snaps} />
    </div>
  );
}

// ---- P0 Master summary ------------------------------------------------------------------------------------------------
const SO_KEY: [SignoffState["state"], string][] = [["signed", "Signed off"], ["awaiting", "Awaiting sign-off"], ["published", "Published, not sent"]];
const SoChip = ({ s }: { s: SignoffState }) => (s.state === "none" || !s.version ? <span className="faint">–</span> : <span className={`pp-so ${s.state}`}>{s.version}</span>);

/** Round 24 #9: P0 status donut — outer ring Open + Critical · Closed · Voided; inner ring Overdue (part of the open ones). */
export function donutParts(st: IssueCounts): { total: number; outer: { key: string; n: number; colour: string }[]; overdue: number } {
  const voided = Math.max(0, st.all - st.total);
  const outer = [{ key: "open", n: st.open + st.critical, colour: "#EF9F27" }, { key: "closed", n: st.closed, colour: "#639922" }, { key: "voided", n: voided, colour: "#c9c6bc" }];
  return { total: outer.reduce((a, o) => a + o.n, 0), outer, overdue: st.overdue };
}

export function StatusDonut({ st }: { st: IssueCounts }) {
  const d = donutParts(st), tot = Math.max(1, d.total);
  let start = 0;
  const key: [string, string, number, string][] = [["open", "Open", d.outer[0].n, "#EF9F27"], ["overdue", "Overdue", d.overdue, "#A32D2D"],
    ["closed", "Closed", d.outer[1].n, "#639922"], ["voided", "Voided", d.outer[2].n, "#c9c6bc"]];
  const od = (d.overdue / tot) * 100;
  return (
    <div className="pp-donut" data-p0-donut="1">
      <svg width="180" height="180" viewBox="0 0 180 180" role="img" aria-label="Issues by status">
        {d.outer.map((o) => { const pct = (o.n / tot) * 100; const el = (
          <circle key={o.key} cx="90" cy="90" r="78" fill="none" stroke={o.colour} strokeWidth="20" pathLength={100}
            strokeDasharray={`${pct.toFixed(2)} ${(100 - pct).toFixed(2)}`} strokeDashoffset={(-start).toFixed(2)} transform="rotate(-90 90 90)" />);
          start += pct; return el; })}
        <circle cx="90" cy="90" r="58" fill="none" stroke="#f0ede6" strokeWidth="10" />
        <circle cx="90" cy="90" r="58" fill="none" stroke="#A32D2D" strokeWidth="10" pathLength={100} strokeDasharray={`${od.toFixed(2)} ${(100 - od).toFixed(2)}`} transform="rotate(-90 90 90)" />
        <text x="90" y="88" textAnchor="middle" fontSize="26" fontWeight="600" fill="#1a1a18">{d.total}</text>
        <text x="90" y="106" textAnchor="middle" fontSize="10.5" fill="#5f5e5a">issues</text>
      </svg>
      <div className="pp-donut-key">{key.map(([k, label, n, c]) => <span key={k} data-stat={k} data-val={n}><i style={{ background: c }} />{label} <b>{n}</b></span>)}</div>
    </div>);
}

/** P0: project totals (donut), open issues by discipline, one row per component with its latest version and sign-off. */
export function MasterPdf({ ov, ctx, reportDate }: { ov: IssuesOverview; ctx: PrintContext; reportDate: string }) {
  const st = ov.stats;
  const max = Math.max(1, ...ov.disciplines.map((d) => st.open_by_discipline[d.code] ?? 0));
  const slots = Math.max(0, MAX_DISCIPLINES - ov.disciplines.length);
  return (
    <div data-pdf="master">
      <section className="pp" data-page="master">
        <div className="pp-body master">
          <div className="pp-issue-head">
            <div><p className="pp-eyebrow">{ctx.report_title} · Master summary</p><h2 className="pp-h">{ctx.name}</h2></div>
            <div className="pp-right"><p className="k">Date</p><p className="v" data-master-date>{formatDate(reportDate)}</p></div>
          </div>
          <div className="pp-p0-top">
            <StatusDonut st={st} />
            <div className="pp-p0-bars">
              <p className="pp-section">Open issues by discipline</p>
              <div className="pp-bars">{ov.disciplines.map((d: Discipline) => {
                const n = st.open_by_discipline[d.code] ?? 0;
                return <div key={d.code}><div className="pp-bar-k"><span>{d.code} · <span className="muted">{d.name}</span></span><b>{n}</b></div>
                  <div className="pp-bar"><div style={{ width: `${Math.round((n / max) * 100)}%`, background: d.colour }} /></div></div>;
              })}</div>
            </div>
          </div>
          <p className="pp-section">By component</p>
          <table className="pp-table master">
            <thead><tr><th>Prefix</th><th>Component</th><th className="num">Total</th>
              {ov.disciplines.map((d) => <th key={d.code} className="num" style={{ color: d.colour }}>{d.code}</th>)}
              {Array.from({ length: slots }, (_, k) => <th key={k} className="num faint">–</th>)}
              <th className="num">Open</th><th className="num">Latest</th><th className="num">Sign-off</th></tr></thead>
            <tbody>{ov.components.map((c) => (
              <tr key={c.id} data-comp={c.prefix}><td className="code">{c.prefix}</td><td>{c.name}</td><td className="num">{c.total}</td>
                {ov.disciplines.map((d) => <td key={d.code} className="num">{c.open_by_discipline[d.code] ?? 0}</td>)}
                {Array.from({ length: slots }, (_, k) => <td key={k} className="num faint">–</td>)}
                <td className="num"><b>{c.open}</b></td><td className="num">{c.signoff.latest ?? <span className="faint">–</span>}</td>
                <td className="num"><SoChip s={c.signoff} /></td></tr>))}
            </tbody>
          </table>
          {/* v0.7.1 (v070-010): "Sign-off legend", fixed just above the footer line however many components there are */}
          <div className="pp-key legend" data-so-legend="1">Sign-off legend:{SO_KEY.map(([s, label]) => <span key={s}><span className={`pp-so ${s}`}>V01</span>{label}</span>)}</div>
        </div>
        <Footer ctx={ctx} page={1} />
      </section>
    </div>
  );
}
